import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, ProgressBar, Spinner, Form, Table } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, addDoc, serverTimestamp, query, where } from 'firebase/firestore';

const StudyTimer = () => {
  const { currentUser } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studyMinutes, setStudyMinutes] = useState('');
  const [studyNote, setStudyNote] = useState('');
  const [studyLogs, setStudyLogs] = useState([]);
  const [loggingStudyTime, setLoggingStudyTime] = useState(false);
  const [studyLogError, setStudyLogError] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadAssignments();
      loadStudyLogs();
    }
  }, [currentUser]);

  const loadAssignments = async () => {
    try {
      setLoading(true);

      // Get user's enrolled modules
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const enrolledModules = userDoc.data()?.enrolledModules || [];

      if (enrolledModules.length > 0) {
        // Fetch assignments for enrolled modules
        const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
        const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(assignment =>
          enrolledModules.includes(assignment.moduleId)
        );
        setAssignments(assignmentsData);
      }
    } catch (error) {
      devError('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudyLogs = async () => {
    try {
      const logsSnapshot = await getDocs(
        query(
          collection(db, 'studyLogs'),
          where('userId', '==', currentUser.uid)
        )
      );
      const logs = logsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate?.() || null
      })).sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      }).slice(0, 5);
      setStudyLogs(logs);
    } catch (error) {
      devError('Error loading study logs:', error);
    }
  };

  const handleLogStudyTime = async () => {
    const minutesValue = parseInt(studyMinutes, 10);
    if (!minutesValue || minutesValue <= 0) {
      setStudyLogError('Please enter the number of minutes studied.');
      return;
    }
    try {
      setLoggingStudyTime(true);
      setStudyLogError('');
      await addDoc(collection(db, 'studyLogs'), {
        userId: currentUser.uid,
        minutes: minutesValue,
        note: studyNote.trim(),
        createdAt: serverTimestamp()
      });
      setStudyMinutes('');
      setStudyNote('');
      await loadStudyLogs();
    } catch (error) {
      devError('Error logging study time:', error);
      setStudyLogError('Failed to log study time. Please try again.');
    } finally {
      setLoggingStudyTime(false);
    }
  };

  const getUserAssignments = () => {
    return assignments.map(assignment => {
      const userSubmission = assignment.submissions?.[currentUser?.uid];
      return {
        ...assignment,
        progress: {
          status: userSubmission?.status || 'not_started',
          progress: userSubmission?.progress || 0,
          submittedAt: userSubmission?.submittedAt,
          score: userSubmission?.score
        }
      };
    });
  };

  const calculateTimeRemaining = (dueDate) => {
    const due = dueDate?.toDate ? dueDate.toDate() : new Date(dueDate);
    const now = currentTime;
    const diff = due - now;

    if (diff <= 0) {
      return { expired: true, text: 'Overdue', className: 'danger' };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 7) {
      return { expired: false, text: `${days} days`, className: 'success' };
    } else if (days > 3) {
      return { expired: false, text: `${days} days ${hours}h`, className: 'warning' };
    } else if (days > 0) {
      return { expired: false, text: `${days}d ${hours}h ${minutes}m`, className: 'warning' };
    } else if (hours > 6) {
      return { expired: false, text: `${hours}h ${minutes}m`, className: 'danger' };
    } else {
      return { expired: false, text: `${hours}h ${minutes}m`, className: 'danger' };
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      critical: 'danger',
      high: 'warning',
      medium: 'info',
      low: 'secondary'
    };
    return variants[priority] || 'secondary';
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      in_progress: 'primary',
      not_started: 'secondary',
      overdue: 'danger'
    };
    return variants[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading assignments...</span>
        </Spinner>
        <p className="mt-2 text-muted">Loading assignments...</p>
      </div>
    );
  }

  const userAssignments = getUserAssignments();
  const pendingAssignments = userAssignments.filter(a => {
    const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
    return a.progress.status !== 'completed' && a.progress.status !== 'submitted' && dueDate > currentTime;
  });
  const overdueAssignments = userAssignments.filter(a => {
    const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
    return a.progress.status !== 'completed' && a.progress.status !== 'submitted' && dueDate <= currentTime;
  });

  return (
    <div className="study-timer">
      <h4 className="mb-4">Assignment Countdown Timer</h4>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Log Study Time</h5>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Minutes Studied</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={studyMinutes}
                onChange={(event) => setStudyMinutes(event.target.value)}
                placeholder="e.g. 45"
              />
            </Col>
            <Col md={6}>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                type="text"
                value={studyNote}
                onChange={(event) => setStudyNote(event.target.value)}
                placeholder="What did you focus on?"
              />
            </Col>
            <Col md={2} className="d-grid">
              <Button
                variant="primary"
                onClick={handleLogStudyTime}
                disabled={loggingStudyTime}
              >
                {loggingStudyTime ? 'Saving...' : 'Log Time'}
              </Button>
            </Col>
          </Row>
          {studyLogError && (
            <div className="text-danger small mt-2">{studyLogError}</div>
          )}
          {studyLogs.length > 0 && (
            <div className="mt-3">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Minutes</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {studyLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.createdAt ? log.createdAt.toLocaleDateString() : '—'}</td>
                      <td>{log.minutes}</td>
                      <td>{log.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {overdueAssignments.length > 0 && (
        <Card className="mb-4 border-danger">
          <Card.Header className="bg-danger text-white">
            <h5 className="mb-0">⚠️ Overdue Assignments ({overdueAssignments.length})</h5>
          </Card.Header>
          <Card.Body>
            {overdueAssignments.map(assignment => {
              const timeRemaining = calculateTimeRemaining(assignment.dueDate);
              return (
                <div key={assignment.id} className="mb-3 p-3 border border-danger rounded">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <h6 className="mb-1">{assignment.title}</h6>
                      <small className="text-muted">{assignment.description}</small>
                    </Col>
                    <Col md={2}>
                      <Badge bg={getPriorityBadge(assignment.priority)} className="me-2">
                        {assignment.priority.toUpperCase()}
                      </Badge>
                    </Col>
                    <Col md={2}>
                      <Badge bg={timeRemaining.className}>
                        {timeRemaining.text}
                      </Badge>
                    </Col>
                    <Col md={2}>
                      <ProgressBar 
                        now={assignment.progress.progress} 
                        label={`${assignment.progress.progress}%`}
                        variant="danger"
                        size="sm"
                      />
                    </Col>
                  </Row>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">Upcoming Assignments ({pendingAssignments.length})</h5>
        </Card.Header>
        <Card.Body>
          {pendingAssignments.length === 0 ? (
            <p className="text-muted mb-0">No pending assignments. Great work!</p>
          ) : (
            pendingAssignments
              .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
              .map(assignment => {
                const timeRemaining = calculateTimeRemaining(assignment.dueDate);
                return (
                  <div key={assignment.id} className="mb-3 p-3 border rounded">
                    <Row className="align-items-center">
                      <Col md={5}>
                        <h6 className="mb-1">{assignment.title}</h6>
                        <small className="text-muted d-block">{assignment.description}</small>
                        <small className="text-info">
                          Due: {new Date(assignment.dueDate).toLocaleDateString()} at {new Date(assignment.dueDate).toLocaleTimeString()}
                        </small>
                      </Col>
                      <Col md={2}>
                        <Badge bg={getPriorityBadge(assignment.priority)} className="me-2">
                          {assignment.priority.toUpperCase()}
                        </Badge>
                        <Badge bg={getStatusBadge(assignment.progress.status)}>
                          {assignment.progress.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </Col>
                      <Col md={2}>
                        <div className="text-center">
                          <Badge bg={timeRemaining.className} className="fs-6 p-2">
                            {timeRemaining.text}
                          </Badge>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-2">
                          <small className="text-muted">Progress: {assignment.progress.progress}%</small>
                          <ProgressBar 
                            now={assignment.progress.progress} 
                            variant={assignment.progress.progress > 75 ? 'success' : assignment.progress.progress > 25 ? 'warning' : 'danger'}
                            size="sm"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          variant={assignment.progress.status === 'not_started' ? 'primary' : 'outline-primary'}
                          className="w-100"
                        >
                          {assignment.progress.status === 'not_started' ? 'Start' : 'Continue'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                );
              })
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Header>
          <h6 className="mb-0">Assignment Progress Summary</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3} className="text-center">
              <h4 className="text-success">{userAssignments.filter(a => a.progress.status === 'completed').length}</h4>
              <small className="text-muted">Completed</small>
            </Col>
            <Col md={3} className="text-center">
              <h4 className="text-primary">{userAssignments.filter(a => a.progress.status === 'in_progress').length}</h4>
              <small className="text-muted">In Progress</small>
            </Col>
            <Col md={3} className="text-center">
              <h4 className="text-warning">{pendingAssignments.length}</h4>
              <small className="text-muted">Pending</small>
            </Col>
            <Col md={3} className="text-center">
              <h4 className="text-danger">{overdueAssignments.length}</h4>
              <small className="text-muted">Overdue</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default StudyTimer;
